<template>
  <div>
    <el-popover :visible="editMode" trigger="click" width="auto">
      <BoxComponent v-click-outside="hide" :p="24" :m="-12">
        <FlexComponent :gap="32" direction="column">
          <FlexComponent :gap="24">
            <FlexComponent v-for="(source, i) in editableCostSources" :key="i" :gap="4" direction="column">
              <TypographyComponent variant="title-large" class="cost-cell__cost-title">
                {{ trafficSourcesTemplates.find((el) => el.source === source.source)?.name }}
              </TypographyComponent>
              <template v-if="source.type === 'CPC'">
                <CpcInput v-model="source.cpc" />
                <TypographyComponent variant="label-large">{{ currencyPerClickLabel }}</TypographyComponent>
                <DiffText
                  :cost="source.cpc"
                  :base-cost="
                    baseTrafficSources.find((el) => el.source === source.source)
                      ? baseTrafficSources.find((el) => el.source === source.source).cpc
                      : baseTrafficSources.find((el) => el.source === 0).cpc
                  "
                  type="cpc"
                />
              </template>
              <template v-if="source.type === 'CPA'">
                <CpaInput v-model="source.cpa" />
                <TypographyComponent variant="label-large">%</TypographyComponent>
                <DiffText
                  :cost="source.cpa"
                  :base-cost="
                    baseTrafficSources.find((el) => el.source === source.source)
                      ? baseTrafficSources.find((el) => el.source === source.source).cpa
                      : baseTrafficSources.find((el) => el.source === 0).cpa
                  "
                  type="cpa"
                />
              </template>
            </FlexComponent>
          </FlexComponent>
          <FlexComponent :gap="8" justify="flex-end">
            <el-button size="default" round text @click="resetToDefault">Reset to default</el-button>
            <ButtonComponent variant="accent" @click="saveChange">Save</ButtonComponent>
          </FlexComponent>
        </FlexComponent>
      </BoxComponent>
      <template #reference>
        <div class="popover-grid-costs">
          <div v-for="(source, i) in costSources" :key="i" class="popover-grid-cost">
            <p class="popover-grid-cost-header">
              {{ trafficSourcesTemplates.find((el) => el.source === source.source)?.name }}
            </p>
            <div class="popover-grid-cost-body">
              <div>
                <template v-if="source.type === 'CPC'">
                  <span>{{ formatMoney(source.cpc) + '/click' }}</span>
                  <DiffText
                    class="popover-grid-cost-body-diff"
                    :cost="source.cpc"
                    :base-cost="
                      baseTrafficSources.find((el) => el.source === source.source)
                        ? baseTrafficSources.find((el) => el.source === source.source).cpc
                        : baseTrafficSources.find((el) => el.source === 0).cpc
                    "
                    type="cpc"
                  />
                </template>
                <template v-if="source.type === 'CPA'">
                  <span>{{ source.cpa }} %</span>
                  <DiffText
                    class="popover-grid-cost-body-diff"
                    :cost="source.cpa"
                    :base-cost="
                      baseTrafficSources.find((el) => el.source === source.source)
                        ? baseTrafficSources.find((el) => el.source === source.source).cpa
                        : baseTrafficSources.find((el) => el.source === 0).cpa
                    "
                    type="cpa"
                  />
                </template>
              </div>
              <el-icon ref="buttonRefs" size="16" @click="open">
                <EditPen />
              </el-icon>
            </div>
          </div>
        </div>
      </template>
    </el-popover>
  </div>
</template>

<script lang="ts">
// @ts-nocheck
import { mapGetters } from 'vuex'

import CpaInput from './CpaInput.vue'
import CpcInput from './CpcInput.vue'
import DiffText from './DiffText.vue'

import EditPen from '@/assets/icons/edit-pen.svg?component'
import { formatFractionAsPercentage, formatMoney, getCurrencySymbol } from '@/legacy/store/helpFunctions'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

export default {
  name: 'CostCell',
  components: {
    FlexComponent,
    BoxComponent,
    TypographyComponent,
    EditPen,
    CpcInput,
    CpaInput,
    DiffText,
  },
  props: {
    trafficSources: {
      type: Array,
      required: true,
    },
    baseTrafficSources: {
      type: Array,
      required: true,
    },
    useCpa: {
      type: Boolean,
      required: true,
    },
    onSave: Function,
  },
  data: function () {
    return {
      editMode: false,
      costSources: [],
      editableCostSources: [],
    }
  },
  computed: {
    ...mapGetters('trafficSources', ['trafficSourcesTemplates']),
    currencyPerClickLabel: function () {
      return getCurrencySymbol() + '/click'
    },
  },
  watch: {
    trafficSources: {
      immediate: true,
      handler() {
        this.costSources = []
        for (let i = 0; i < this.trafficSources.length; i++) {
          let pushCondition = true
          const temp = { ...this.trafficSources[i] }
          if (temp.source === 0) {
            if (temp.cpc) {
              temp.cpc = Number(Number(Number(temp.cpc).toFixed(0) / 100).toFixed(2))
            }
            if (temp.invoice_cpc) {
              temp.invoice_cpc = Number(Number(Number(temp.invoice_cpc).toFixed(0) / 100).toFixed(2))
            }
            if (temp.cpa) {
              temp.cpa = Number(Number(Number(temp.cpa).toFixed(2) * 100).toFixed(0))
            }
            if (temp.invoice_cpa) {
              temp.invoice_cpa = Number(Number(Number(temp.invoice_cpa).toFixed(2) * 100).toFixed(0))
            }
            temp.type = temp.cpa ? 'CPA' : 'CPC'
            this.costSources.push(temp)
          } else {
            if (temp.cpc) {
              temp.cpc = Number(Number(Number(temp.cpc).toFixed(0) / 100).toFixed(2))
              if (
                temp.cpc.toFixed(2) === Number(Number(this.trafficSources.find((el) => el.source === 0).cpc).toFixed(0) / 100).toFixed(2)
              ) {
                pushCondition = false
              }
            }
            if (temp.invoice_cpc) {
              temp.invoice_cpc = Number(Number(Number(temp.invoice_cpc).toFixed(0) / 100).toFixed(2))
            }
            if (temp.cpa) {
              temp.cpa = Number(Number(Number(temp.cpa).toFixed(2) * 100).toFixed(0))
              if (
                temp.cpa.toFixed(0) === Number(Number(this.trafficSources.find((el) => el.source === 0).cpa).toFixed(2) * 100).toFixed(0)
              ) {
                pushCondition = false
              }
            }
            if (temp.invoice_cpa) {
              temp.invoice_cpa = Number(Number(Number(temp.invoice_cpa).toFixed(2) * 100).toFixed(0))
            }
            temp.type = temp.cpa ? 'CPA' : 'CPC'
            if (temp.cpc === null && this.trafficSources.find((el) => el.source === 0).cpc === null) {
              pushCondition = false
            }
            if (pushCondition) {
              this.costSources.push(temp)
            }
          }
        }
      },
    },
  },
  methods: {
    open(e) {
      this.editMode = true
      this.normalizeEditableCosts()
      e.stopPropagation()
    },
    hide() {
      if (this.editMode) {
        this.editMode = false
      }
    },
    normalizeEditableCosts() {
      this.editableCostSources = []
      for (let i = 0; i < this.trafficSourcesTemplates.length; i++) {
        const temp = {
          ...this.trafficSources.find((el) => el.source === this.trafficSourcesTemplates[i].source),
        }
        if (!temp || !Object.keys(temp).length) {
          const newEL = {}
          const baseEl = { ...this.trafficSources.find((el) => el.source === 0) }
          if (baseEl.cpc || baseEl.cpc === 0) {
            newEL.cpc = Number(Number(Number(baseEl.cpc).toFixed(0) / 100).toFixed(2))
          } else {
            newEL.cpc = null
          }
          if (baseEl.invoice_cpc) {
            newEL.invoice_cpc = Number(Number(Number(baseEl.invoice_cpc).toFixed(0) / 100).toFixed(2))
          } else {
            newEL.invoice_cpc = null
          }
          if (baseEl.cpa) {
            newEL.cpa = Number(Number(Number(baseEl.cpa).toFixed(2) * 100).toFixed(0))
          } else {
            newEL.cpa = null
          }
          if (baseEl.invoice_cpa) {
            newEL.invoice_cpa = Number(Number(Number(baseEl.invoice_cpa).toFixed(2) * 100).toFixed(0))
          } else {
            newEL.invoice_cpa = null
          }
          newEL.source = this.trafficSourcesTemplates[i].source
          newEL.type = newEL.cpa ? 'CPA' : 'CPC'
          this.editableCostSources.push(newEL)
        } else {
          if (temp.cpc) {
            temp.cpc = Number(Number(Number(temp.cpc).toFixed(0) / 100).toFixed(2))
          }
          if (temp.invoice_cpc) {
            temp.invoice_cpc = Number(Number(Number(temp.invoice_cpc).toFixed(0) / 100).toFixed(2))
          }
          if (temp.cpa) {
            temp.cpa = Number(Number(Number(temp.cpa).toFixed(2) * 100).toFixed(0))
          }
          if (temp.invoice_cpa) {
            temp.invoice_cpa = Number(Number(Number(temp.invoice_cpa).toFixed(2) * 100).toFixed(0))
          }
          temp.type = temp.cpa ? 'CPA' : 'CPC'
          this.editableCostSources.push(temp)
        }
      }
    },
    resetToDefault() {
      this.editableCostSources.forEach((item) => {
        const baseSourceValue = this.baseTrafficSources.find((el) => el.source === item.source)
        if (item.type === 'CPC') {
          item.cpc = baseSourceValue.cpc
          item.invoice_cpc = baseSourceValue.invoice_cpc
        }
        if (item.type === 'CPA') {
          item.cpa = baseSourceValue.cpa
          item.invoice_cpa = baseSourceValue.invoice_cpa
        }
      })
    },
    saveChange() {
      const tempArr = []
      for (let i = 0; i < this.editableCostSources.length; i++) {
        if (this.editableCostSources[i].source === 0) {
          tempArr.push(this.editableCostSources[i])
        } else {
          if (this.editableCostSources[i].type === 'CPC') {
            if (this.editableCostSources[i].cpc !== this.editableCostSources.find((el) => el.source === 0).cpc) {
              tempArr.push(this.editableCostSources[i])
            }
          }
          if (this.editableCostSources[i].type === 'CPA') {
            if (this.editableCostSources[i].cpa !== this.editableCostSources.find((el) => el.source === 0).cpa) {
              tempArr.push(this.editableCostSources[i])
            }
          }
        }
      }
      this.costSources = tempArr
      this.editMode = false
      this.onSave(this.costSources)
    },
    formatMoney,
    formatFractionAsPercentage,
  },
}
</script>

<style scoped lang="scss">
.cost-cell__cost-title {
  margin-bottom: 4px;
}
.popover-grid {
  &-costs {
    display: flex;
    gap: 16px;
  }
  &-cost {
    display: flex;
    flex-direction: column;
    &-header {
      margin-bottom: 0;
      text-align: start;
      color: #212529;
      font-size: 12px;
    }
    &-body {
      display: flex;
      gap: 4px;
      &-diff {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
      i {
        margin-top: 3px;
      }
    }
  }
}
</style>
